import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { TooltipBodyProps } from "@design-system/Tooltip/Tooltip.types";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./TooltipPlayground.module.scss";

export const FIGMA_SAMPLE_CONTENT =
    "Aenean lacinia bibendum nulla sed consectetur lorem ipsum dolor sit.";
export const FIGMA_SAMPLE_TITLE = "Title for the tooltip";
export const FIGMA_SAMPLE_BULLETS = [
    "Etiam porta sem malesuada",
    "Magna mollis euismod",
    "Lorem ipsum dolor sit amet",
];
export const FIGMA_SHORTCUT_LABEL = "Aenean lacinia";

const matrixVariants: ReadonlyArray<{ label: string; body: TooltipBodyProps }> =
    [
        {
            label: "Title=No, Shortcut=No, Bullets=No",
            body: { content: FIGMA_SAMPLE_CONTENT },
        },
        {
            label: "Title=No, Shortcut=One, Bullets=No",
            body: { content: FIGMA_SHORTCUT_LABEL, shortcut: "a" },
        },
        {
            label: "Title=No, Shortcut=Two, Bullets=No",
            body: { content: FIGMA_SHORTCUT_LABEL, shortcut: "mod+a" },
        },
        {
            label: "Title=No, Shortcut=Three, Bullets=No",
            body: { content: FIGMA_SHORTCUT_LABEL, shortcut: "a>a" },
        },
        {
            label: "Title=Yes, Shortcut=No, Bullets=No",
            body: { title: FIGMA_SAMPLE_TITLE, content: FIGMA_SAMPLE_CONTENT },
        },
        {
            label: "Title=Yes, Shortcut=No, Bullets=Yes",
            body: {
                title: FIGMA_SAMPLE_TITLE,
                content: FIGMA_SAMPLE_CONTENT,
                bullets: FIGMA_SAMPLE_BULLETS,
            },
        },
        {
            label: "Title=No, Shortcut=No, Bullets=Yes",
            body: {
                content: FIGMA_SAMPLE_CONTENT,
                bullets: FIGMA_SAMPLE_BULLETS,
            },
        },
        {
            label: "Shortcut only (code-only, PRO-12196 T11)",
            body: { shortcut: "mod+a" },
        },
        {
            label: "Presence, live (4822:8969)",
            body: {
                presence: [
                    {
                        id: "agent",
                        name: "Kick",
                        detail: "Working now",
                        isLive: true,
                    },
                ],
            },
        },
        {
            label: "Presence, idle (4822:9018)",
            body: {
                presence: [
                    {
                        id: "matt",
                        name: "Matt",
                        detail: "Last active 2h ago",
                        isLive: false,
                    },
                ],
            },
        },
        {
            label: "Presence, +N list (code-only)",
            body: {
                presence: [
                    {
                        id: "mo",
                        name: "Mo R.",
                        detail: "Viewing now",
                        isLive: true,
                    },
                    {
                        id: "carla",
                        name: "Carla C.",
                        detail: "Last active 2h ago",
                        isLive: false,
                    },
                ],
            },
        },
    ];

/**
 * Figma's seven Tooltip variants plus the shortcut-only and board presence
 * cases, each forced open under its own trigger for side-by-side screenshots
 * against 3971:1089, 4822:8969 and 4822:9018.
 */
export const TooltipPlaygroundMatrix = () => (
    <VStack className={styles.section} gap={24} padding={24}>
        <h2 className={styles.sectionTitle}>Figma matrix</h2>
        <div className={styles.matrixGrid}>
            {matrixVariants.map(({ label, body }) => (
                <div key={label} className={styles.matrixCell}>
                    <span className={styles.matrixLabel}>{label}</span>
                    <Tooltip {...body} open side="bottom" align="start">
                        <Button variant="secondary" size="xs">
                            Trigger
                        </Button>
                    </Tooltip>
                </div>
            ))}
        </div>
    </VStack>
);
